import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Star, ChevronLeft, ChevronRight, Image as ImageIcon, Film, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { listMedia, uploadMedia, deleteMedia, reorderMedia, patchMedia, mediaUrl } from "@/lib/api";

const ACCEPT = ".jpg,.jpeg,.png,.webp,.mp4,.mov,.webm";

/** "Materiali del cliente": upload, preview, delete, reorder, select, cover. Nothing more. */
export function ClientMedia({ onChange }) {
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);

  const apply = (d) => { setItems(d.items || []); onChange?.(d.items || []); };
  useEffect(() => { listMedia().then(apply).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (let i = 0; i < files.length; i++) {
      try {
        setProgress({ name: files[i].name, pct: 0, i: i + 1, n: files.length });
        await uploadMedia(files[i], (pct) => setProgress((p) => ({ ...p, pct })));
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Caricamento fallito: ${files[i].name}`);
      }
    }
    setProgress(null);
    const d = await listMedia();
    apply(d);
    toast.success(`${files.length} file caricat${files.length === 1 ? "o" : "i"}`);
  };

  const move = async (idx, dir) => {
    const ids = items.map((m) => m.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    apply(await reorderMedia(ids));
  };
  const del = async (id) => apply(await deleteMedia(id));
  const toggle = async (m) => apply(await patchMedia(m.id, { selected: !m.selected }));
  const cover = async (m) => apply(await patchMedia(m.id, { cover: true }));

  const photos = items.filter((m) => m.kind === "photo").length;
  const videos = items.length - photos;
  const used = items.filter((m) => m.selected).length;

  return (
    <div className="nadia-card p-5 mb-6" data-testid="client-media-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-heading text-lg font-semibold text-slate-100">Materiali del cliente</div>
          <div className="text-xs text-slate-400 mt-0.5">Carica le tue foto e i tuoi video: Nadia li usa per primi nel reel. Se non bastano, integra con materiale del sito o AI.</div>
          <div className="mt-2 flex gap-2 flex-wrap text-[10px] uppercase tracking-wider font-semibold">
            <Tag testId="media-count-photos"><ImageIcon size={10} /> Foto {photos}</Tag>
            <Tag testId="media-count-videos"><Film size={10} /> Video {videos}</Tag>
            <Tag accent testId="media-count-used"><Check size={10} /> Usati nel reel {used}</Tag>
          </div>
        </div>
        <div>
          <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={onFiles} data-testid="media-file-input" />
          <Button onClick={() => inputRef.current?.click()} disabled={!!progress} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold" data-testid="media-upload-button">
            {progress ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
            Carica foto o video
          </Button>
          <div className="text-[10px] text-slate-500 mt-1 text-right">jpg, png, webp · mp4, mov, webm</div>
        </div>
      </div>

      {progress && (
        <div className="mt-4" data-testid="media-upload-progress">
          <div className="flex justify-between text-xs text-slate-400 mb-1"><span className="truncate">{progress.name} ({progress.i}/{progress.n})</span><span>{progress.pct}%</span></div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-cyan-400 to-violet-500 transition-[width]" style={{ width: `${progress.pct}%` }} /></div>
        </div>
      )}

      {items.length === 0 && !progress ? (
        <button onClick={() => inputRef.current?.click()} className="mt-4 w-full rounded-xl border border-dashed border-white/10 py-8 text-sm text-slate-500 hover:border-cyan-400/40 hover:text-slate-300 transition-colors" data-testid="media-empty">
          Nessun file ancora. Tocca per caricare le prime foto o clip.
        </button>
      ) : (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="media-grid">
          {items.map((m, idx) => (
            <div key={m.id} className={`group relative rounded-xl overflow-hidden border ${m.selected ? "border-cyan-500/40" : "border-white/10 opacity-60"} bg-black aspect-[4/5]`} data-testid={`media-item-${m.id}`}>
              {m.kind === "video" ? (
                <video src={mediaUrl(m.url)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
              ) : (
                <img src={mediaUrl(m.url)} alt={m.filename} className="w-full h-full object-cover" />
              )}
              <div className="absolute top-2 left-2 flex gap-1">
                <span className="px-1.5 py-0.5 rounded bg-black/60 text-[9px] uppercase tracking-wider font-bold text-white">{m.kind === "video" ? "Video" : "Foto"}</span>
                {m.cover && <span className="px-1.5 py-0.5 rounded bg-amber-400 text-[9px] uppercase tracking-wider font-bold text-slate-950" data-testid={`media-cover-${m.id}`}>Cover</span>}
              </div>
              <button onClick={() => toggle(m)} className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center border ${m.selected ? "bg-cyan-400 text-slate-950 border-cyan-300" : "bg-black/60 text-white border-white/30"}`} title={m.selected ? "Usato nel reel" : "Escluso"} data-testid={`media-toggle-${m.id}`}>
                <Check size={12} strokeWidth={3} />
              </button>
              <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/85 to-transparent">
                <div className="text-[10px] text-white/80 truncate">{idx + 1}. {m.filename}</div>
                <div className="mt-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Mini onClick={() => move(idx, -1)} testId={`media-left-${m.id}`}><ChevronLeft size={11} /></Mini>
                  <Mini onClick={() => move(idx, 1)} testId={`media-right-${m.id}`}><ChevronRight size={11} /></Mini>
                  <Mini onClick={() => cover(m)} testId={`media-set-cover-${m.id}`}><Star size={11} /></Mini>
                  <Mini onClick={() => del(m.id)} danger testId={`media-delete-${m.id}`}><Trash2 size={11} /></Mini>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Tag({ children, accent, testId }) {
  return <span data-testid={testId} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${accent ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/25" : "bg-white/[0.03] text-slate-400 border-white/10"}`}>{children}</span>;
}

function Mini({ children, onClick, danger, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`w-6 h-6 rounded-md flex items-center justify-center bg-black/60 border border-white/20 text-white ${danger ? "hover:bg-rose-500/80" : "hover:bg-cyan-500/70"}`}>
      {children}
    </button>
  );
}
